import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import {
  Col,
  Row,
  Container,
  Button,
  Modal,
  ModalBody,
} from "reactstrap"

import Breadcrumbs from "../../components/Common/Breadcrumb2"
import { withTranslation } from "react-i18next"
import "../../components/Common/ts-css.css"

import { get, post, put } from "helpers/api_helper"

import * as Yup from "yup"
import { useFormik } from "formik"

import Spinners from "components/Common/Spinner"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import moment from "moment"

// Step Components
import Step1 from "./components/Step1"
import Step2 from "./components/Step2"
import Step3 from "./components/Step3"
import EstimateCost from "./components/EstimateCost"
import ConfirmSendOrderModal from "./components/ConfirmSendOrderModal"
import DraftSuccessModal from "./components/DraftSuccessModal"

// Panel Images
import midUpSvg from "../../assets/images/yj-panel/mid-up.svg"
import commonSvg from "../../assets/images/yj-panel/common.svg"

// Demographic Options
import { createInitialDemographicState } from "./data/demographicOptions"

const CancelModal = ({ show, onConfirm, onCancel }) => {
  return (
    <Modal size="md" isOpen={show} toggle={onCancel} centered={true}>
      <ModalBody className="px-4 py-4">
        <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to cancel?</h5>
        
        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ 
              minWidth: "100px", 
              backgroundColor: "#fff", 
              border: "1px solid #ced4da",
              color: "#495057",
              borderRadius: "8px"
            }}
            onClick={onConfirm}
          >
            Yes
          </button>
          <button
            type="button"
            className="btn btn-success"
            style={{ 
              minWidth: "100px",
              backgroundColor: "#34c38f",
              border: "1px solid #34c38f",
              borderRadius: "8px"
            }}
            onClick={onCancel}
          >
            No
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

// Demographic Error Modal Component
const DemographicErrorModal = ({ show, message, onConfirm }) => {
  return (
    <Modal size="md" isOpen={show} toggle={onConfirm} centered={true}>
      <ModalBody className="px-4 py-4 text-center">
        <i className="bx bx-error-circle text-danger mb-3" style={{ fontSize: "48px" }}></i>
        <h5 className="mb-4" style={{ fontWeight: "600" }}>Validation Error</h5>
        <p className="text-muted">{message}</p>
        
        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-success"
            style={{ 
              minWidth: "120px",
              backgroundColor: "#34c38f",
              border: "1px solid #34c38f",
              borderRadius: "8px"
            }}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            OK
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

// Demographic Category Keys for validation
const categoriesConfig = [
  { key: "gender" },
  { key: "age" },
  { key: "marriage" },
  { key: "education" },
  { key: "personalMonthlyIncome" },
  { key: "familyMonthlyIncome" },
  { key: "avgMonthlySpending" },
  { key: "spendingHabits" },
  { key: "interestType" },
  { key: "hobbiesInterests" },
]

const additionalBasicCategories = [
  { key: "appCategories" },
  { key: "livingArrangement" },
  { key: "transportation" },
]

const carRelatedCategories = [
  { key: "carOwner" },
  { key: "fuelType" },
  { key: "carType" },
  { key: "paymentMethod" },
  { key: "purchaseTime" },
  { key: "mileageDriven" },
  { key: "purchaseStatus" },
  { key: "purchaseChannel" },
  { key: "vehiclesOwned" },
]

const plannedPurchaseCategories = [
  { key: "plannedPurchaseTime" },
  { key: "plannedPowertrainType" },
  { key: "plannedVehicleType" },
  { key: "plannedPurchaseStatus" },
  { key: "carInfoSources" },
  { key: "purchaseDecisionFactors" },
]

const SurveyOrderCampaignTemplate = ({ pageView = "create" }) => {
  const navigate = useNavigate()
  const { campaignId } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [showPanelModal, setShowPanelModal] = useState(false)
  
  // Popup States
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false)
  const [showDraftSuccessModal, setShowDraftSuccessModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showDemographicErrorModal, setShowDemographicErrorModal] = useState(false)
  const [demographicErrorMessage, setDemographicErrorMessage] = useState("")

  // Step 2 States
  const [demographicCards, setDemographicCards] = useState([])

  // Campaign ID for URL (next_id for create/duplicate, campaignId for update)
  const [cidForUrl, setCidForUrl] = useState(null)

  // Step 3 States
  const [categoryOptions, setCategoryOptions] = useState([])
  const [allSurveyOptions, setAllSurveyOptions] = useState([])

  // Countries that have panel options available
  const countriesWithPanels = ["cn"]

  const validationSchema = Yup.object({
    // Step 1
    countryTarget: Yup.string().required("Please Select Country Target"),
    panelOption: Yup.string().required("Please Select Panel Option"),
    panelIntroductions: Yup.string().max(1000),
    // Step 2
    sampleType: Yup.string().required("Please Select Sample Type"),
    totalSamples: Yup.string().required("Please Enter Total Samples"),
    totalMaximumSamples: Yup.string().required("Please Enter Total Maximum Samples"),
    // Contact Information
    company: Yup.string().max(255).required("Please Enter Company"),
    personInCharge: Yup.string().max(255).required("Please Enter Person In Charge"),
    email: Yup.string().email("Invalid email").required("Please Enter Email"),
    telephone: Yup.string().max(20).required("Please Enter Telephone"),
    // Step 3
    categoryId: Yup.string().required("Please Select Category"),
    surveyId: Yup.string().required("Please Select Survey"),
    setFilterQuestion: Yup.number().required("Please Enter Set Filter Question").min(0),
    addonPointsReward: Yup.number().nullable().min(0),
    campaignName: Yup.string().required("Please Enter Campaign Name"),
    startDate: Yup.date()
      .required("Please Select Start Date")
      .min(moment().startOf('day'), "Start date must be today or later"),
    endDate: Yup.date()
      .required("Please Select End Date")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  })

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: ["create", "duplicate"].includes(pageView) ? undefined : "",
      // Step 1
      countryTarget: "",
      panelOption: "",
      panelIntroductions: "",
      // Step 2
      sampleType: "Car Owner Samples",
      totalSamples: "",
      totalMaximumSamples: "",
      // Contact Information
      company: "",
      personInCharge: "",
      email: "",
      telephone: "",
      // Step 3
      categoryId: "",
      surveyId: "",
      totalSurveyQuestions: "",
      fixQuestions: "",
      aiAddonQuestions: "",
      setFilterQuestion: "",
      filterQuestionsText: "",
      testUrl: "",
      endPageUrl: "",
      addonPointsReward: "",
      showDisclaimer: false,
      campaignName: "",
      startDate: "",
      endDate: "",
      status: "draft",
      campaignStatus: ["create", "duplicate"].includes(pageView) ? "not_yet" : "",
      // Step 4 (auto-calculated from panel + total questions)
      estimateCostUnit: "RMB",
      estimateCostFee: "0.00",
      estimateCostBasicRewards: "0.00",
      estimateCostAddOnRewards: "0.00",
      estimateCostTotalRewards: "0.00",
      estimateCostManagementFee: "0.00",
      estimateCostTotal: "0.00",
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      console.log("🚀 ~ onSubmit ~ values:", values)

      const payload = {
        campaign_name: values.campaignName,
        country_target: values.countryTarget,
        panel_option: values.panelOption,
        sample_type: values.sampleType,
        total_samples: (values.totalSamples !== "" && values.totalSamples !== null) ? Number(values.totalSamples) : null,
        total_maximum_samples: (values.totalMaximumSamples !== "" && values.totalMaximumSamples !== null) ? Number(values.totalMaximumSamples) : null,
        demographic_cards: demographicCards,
        company: values.company,
        person_in_charge: values.personInCharge,
        email: values.email,
        telephone: values.telephone,
        category_id: values.categoryId ? Number(values.categoryId) : null,
        survey_id: values.surveyId ? Number(values.surveyId) : null,
        set_filter_question: (values.setFilterQuestion !== "" && values.setFilterQuestion !== null) ? Number(values.setFilterQuestion) : null,
        filter_questions_text: values.filterQuestionsText || "",
        test_url: values.testUrl,
        end_page_url: values.endPageUrl,
        addon_points_reward: (values.addonPointsReward !== "" && values.addonPointsReward !== null) ? Number(values.addonPointsReward) : null,
        show_disclaimer: values.showDisclaimer,
        start_date: values.startDate ? moment.utc(values.startDate).toISOString() : null,
        end_date: values.endDate ? moment.utc(values.endDate).toISOString() : null,
        status: values.status,
        campaign_status: values.campaignStatus,
        estimate_cost_unit: values.estimateCostUnit || "RMB",
        estimate_cost_fee: (values.estimateCostFee !== "" && values.estimateCostFee !== null) ? Number(values.estimateCostFee) : 0,
        estimate_cost_basic_rewards: (values.estimateCostBasicRewards !== "" && values.estimateCostBasicRewards !== null) ? Number(values.estimateCostBasicRewards) : 0,
        estimate_cost_add_on_rewards: (values.estimateCostAddOnRewards !== "" && values.estimateCostAddOnRewards !== null) ? Number(values.estimateCostAddOnRewards) : 0,
        estimate_cost_total_rewards: (values.estimateCostTotalRewards !== "" && values.estimateCostTotalRewards !== null) ? Number(values.estimateCostTotalRewards) : 0,
        estimate_cost_management_fee: (values.estimateCostManagementFee !== "" && values.estimateCostManagementFee !== null) ? Number(values.estimateCostManagementFee) : 0,
        estimate_cost_total: (values.estimateCostTotal !== "" && values.estimateCostTotal !== null) ? Number(values.estimateCostTotal) : 0,
      }

      try {
        if (["create", "duplicate"].includes(pageView)) {
          await post(
            process.env.REACT_APP_BACKEND_URL + "/v1/admin/campaign",
            payload
          )
        } else {
          await put(
            process.env.REACT_APP_BACKEND_URL +
              "/v1/admin/campaign/" +
              campaignId,
            payload
          )
        }
        
        // Show draft success modal if status is draft
        if (values.status === "draft") {
          setShowDraftSuccessModal(true)
        } else {
          // Navigate to list page for pending status
          navigate("/survey-order-campaign")
        }
      } catch (error) {
        console.log("error", error)
      }
    },
  })

  // Mock data for dropdowns
  const countryOptions = [
    { value: "", label: "-- Select Country --" },
    { value: "th", label: "Thailand" },
    { value: "cn", label: "China" },
    { value: "tw", label: "Taiwan" },
    { value: "vn", label: "Vietnam" },
    { value: "jp", label: "Japan" },
  ]

  // All panel options with country association
  const allPanelOptions = [
    { value: "", label: "-- Select Panel --", image: "", country: null },
    { value: "YJ Mid-Up class Sample Pool", label: "YJ Mid-Up class Sample Pool", image: midUpSvg, country: "cn" },
    { value: "YJ Common Consumer Sample Pool", label: "YJ Common Consumer Sample Pool", image: commonSvg, country: "cn" },
  ]

  // Filter panel options based on selected country
  const panelOptions = useMemo(() => {
    const selectedCountry = validation.values.countryTarget
    if (!selectedCountry) {
      // Return only the placeholder when no country is selected
      return [{ value: "", label: "-- Select Panel --", image: "", country: null }]
    }
    // Filter panels for the selected country + placeholder
    const filteredPanels = allPanelOptions.filter(
      panel => panel.country === null || panel.country === selectedCountry
    )
    return filteredPanels
  }, [validation.values.countryTarget])

  // Step 3 Filtered Survey Options
  const filteredSurveyOptions = useMemo(() => {
    const selectedCategoryId = validation.values.categoryId
    if (!selectedCategoryId) return []

    const selectedCategory = categoryOptions.find(cat => String(cat.id) === String(selectedCategoryId))
    if (!selectedCategory) return []

    return allSurveyOptions.filter(survey => survey.category_name === selectedCategory.category_name)
  }, [validation.values.categoryId, categoryOptions, allSurveyOptions])

  const sampleTypeOptions = [
    { value: "", label: "-- Select Sample Type --" },
    { value: "Car Owner Samples", label: "Car Owner Samples" },
  ]

  const preFillForm = (formData = {}) => {
    const getVal = (val, defaultVal = "") => (val !== null && val !== undefined ? val : defaultVal)

    validation.setFieldValue("id", formData.id)
    validation.setFieldValue("countryTarget", formData.country_target || "")
    validation.setFieldValue("panelOption", formData.panel_option || "")
    validation.setFieldValue("panelIntroductions", formData.panel_introductions || "")
    validation.setFieldValue("sampleType", formData.sample_type || "")
    validation.setFieldValue("totalSamples", getVal(formData.total_samples))
    validation.setFieldValue("totalMaximumSamples", getVal(formData.total_maximum_samples))
    
    validation.setFieldValue("company", formData.company || "")
    validation.setFieldValue("personInCharge", formData.person_in_charge || "")
    validation.setFieldValue("email", formData.email || "")
    validation.setFieldValue("telephone", formData.telephone || "")
    
    validation.setFieldValue("categoryId", formData.category_id || "")
    validation.setFieldValue("surveyId", formData.survey_id || "")
    validation.setFieldValue("setFilterQuestion", getVal(formData.set_filter_question))
    validation.setFieldValue("filterQuestionsText", formData.filter_questions_text || "")
    validation.setFieldValue("testUrl", formData.test_url || "")
    validation.setFieldValue("endPageUrl", formData.end_page_url || "")
    validation.setFieldValue("addonPointsReward", getVal(formData.addon_points_reward))
    validation.setFieldValue("showDisclaimer", formData.show_disclaimer || false)
    validation.setFieldValue("campaignName", formData.campaign_name || "")
    
    if (formData.start_date) {
      validation.setFieldValue("startDate", moment.utc(formData.start_date).format("YYYY-MM-DD"))
    }
    if (formData.end_date) {
      validation.setFieldValue("endDate", moment.utc(formData.end_date).format("YYYY-MM-DD"))
    }
    
    validation.setFieldValue("status", formData.status || "draft")
    validation.setFieldValue("campaignStatus", formData.campaign_status || "")

    validation.setFieldValue("estimateCostUnit", formData.estimate_cost_unit || "RMB")
    validation.setFieldValue("estimateCostFee", getVal(formData.estimate_cost_fee))
    validation.setFieldValue("estimateCostBasicRewards", getVal(formData.estimate_cost_basic_rewards))
    validation.setFieldValue("estimateCostAddOnRewards", getVal(formData.estimate_cost_add_on_rewards))
    validation.setFieldValue("estimateCostTotalRewards", getVal(formData.estimate_cost_total_rewards))
    validation.setFieldValue("estimateCostManagementFee", getVal(formData.estimate_cost_management_fee))
    validation.setFieldValue("estimateCostTotal", getVal(formData.estimate_cost_total))

    if (formData.demographic_cards) {
      setDemographicCards(formData.demographic_cards)
    }
  }

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [catResult, surveyResult] = await Promise.all([
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/category"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/questionnaire")
        ])
        if (catResult && catResult.data) setCategoryOptions(catResult.data)
        if (surveyResult && surveyResult.data) setAllSurveyOptions(surveyResult.data)
      } catch (error) {
        console.error("Error fetching dropdown data:", error)
      }
    }
    fetchDropdownData()

    if (["create", "duplicate"].includes(pageView)) {
      const fetchNextId = async () => {
        try {
          const result = await get(
            process.env.REACT_APP_BACKEND_URL + "/v1/admin/campaign/next-id"
          )
          if (result?.data?.next_id) {
            setCidForUrl(result.data.next_id)
          }
        } catch (error) {
          console.error("Error fetching next campaign id:", error)
        }
      }
      fetchNextId()
    } else if (["update", "view"].includes(pageView) && campaignId) {
      setCidForUrl(campaignId)
    }

    if (["update", "view", "duplicate"].includes(pageView) && campaignId) {
      const fetchData = async () => {
        try {
          const result = await get(
            process.env.REACT_APP_BACKEND_URL +
              "/v1/admin/campaign/" +
              campaignId
          )
          const formData = result.data
          
          // For duplicate mode, modify the data before prefilling
          if (pageView === "duplicate") {
            formData.id = undefined
            formData.campaign_name = (formData.campaign_name || "") + " (Copy)"
            formData.status = "draft"
            formData.campaign_status = "not_yet"
            formData.start_date = null
            formData.end_date = null
          }
          
          preFillForm(formData)
        } catch (error) {
          console.log("error", error)
        }
        setIsLoading(false)
      }
      fetchData()
    } else {
      setIsLoading(false)
    }
  }, [pageView, campaignId])

  // Clear panelOption when countryTarget changes (only during create/edit, not initial load)
  const [prevCountryTarget, setPrevCountryTarget] = useState("")
  useEffect(() => {
    if (prevCountryTarget !== validation.values.countryTarget && prevCountryTarget !== "") {
      // Country changed, clear the panel option
      validation.setFieldValue("panelOption", "")
    }
    setPrevCountryTarget(validation.values.countryTarget)
  }, [validation.values.countryTarget])

  // Clear surveyId when categoryId changes
  const [prevCategoryId, setPrevCategoryId] = useState("")
  useEffect(() => {
    if (prevCategoryId !== validation.values.categoryId && prevCategoryId !== "") {
      validation.setFieldValue("surveyId", "")
    }
    setPrevCategoryId(validation.values.categoryId)
  }, [validation.values.categoryId])

  // Auto-fill questions data when surveyId changes
  const [prevSurveyId, setPrevSurveyId] = useState("")
  useEffect(() => {
    if (prevSurveyId !== validation.values.surveyId && validation.values.surveyId !== "") {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey) {
        validation.setFieldValue("totalSurveyQuestions", selectedSurvey.total_question || 0)
        validation.setFieldValue("fixQuestions", selectedSurvey.fix_question || 0)
        validation.setFieldValue("aiAddonQuestions", selectedSurvey.ai_question || 0)
        
        const campaignParam = cidForUrl
          ? `?campaign_id=${encodeURIComponent(cidForUrl)}&identity=phone`
          : "?identity=phone"
        const testUrl = `${process.env.REACT_APP_FRONTWEB_URL}/pdpa/chat/${selectedSurvey.uuid_link}${campaignParam}`
        validation.setFieldValue("testUrl", testUrl)

        const endPageUrl = `${process.env.REACT_APP_FRONTWEB_URL}/survey/end/${selectedSurvey.uuid_link}`
        validation.setFieldValue("endPageUrl", endPageUrl)
      }
    } else if (validation.values.surveyId === "") {
      validation.setFieldValue("totalSurveyQuestions", "")
      validation.setFieldValue("fixQuestions", "")
      validation.setFieldValue("aiAddonQuestions", "")
      validation.setFieldValue("testUrl", "")
      validation.setFieldValue("endPageUrl", "")
    }
    setPrevSurveyId(validation.values.surveyId)
  }, [validation.values.surveyId, allSurveyOptions, cidForUrl])

  // Auto-fill questions data from dropdown when in edit/duplicate mode (after dropdown data is loaded)
  useEffect(() => {
    if (["update", "view", "duplicate"].includes(pageView) && validation.values.surveyId && allSurveyOptions.length > 0) {
      const selectedSurvey = allSurveyOptions.find(s => String(s.id) === String(validation.values.surveyId))
      if (selectedSurvey) {
        // Auto-fill from dropdown to ensure data is up-to-date
        validation.setFieldValue("totalSurveyQuestions", selectedSurvey.total_question || 0)
        validation.setFieldValue("fixQuestions", selectedSurvey.fix_question || 0)
        validation.setFieldValue("aiAddonQuestions", selectedSurvey.ai_question || 0)
        
        if (selectedSurvey.uuid_link) {
          const campaignParam = cidForUrl
            ? `?campaign_id=${encodeURIComponent(cidForUrl)}&identity=phone`
            : "?identity=phone"
          const testUrl = `${process.env.REACT_APP_FRONTWEB_URL}/pdpa/chat/${selectedSurvey.uuid_link}${campaignParam}`
          validation.setFieldValue("testUrl", testUrl)

          const endPageUrl = `${process.env.REACT_APP_FRONTWEB_URL}/survey/end/${selectedSurvey.uuid_link}`
          validation.setFieldValue("endPageUrl", endPageUrl)
        }
      }
    }
  }, [pageView, allSurveyOptions, validation.values.surveyId, cidForUrl])

  // Pre-fill Step 3 category & survey when coming from questionnaire list (create with location state)
  const location = useLocation()
  const hasAppliedListState = React.useRef(false)
  useEffect(() => {
    if (
      pageView !== "create" ||
      !location.state ||
      hasAppliedListState.current ||
      categoryOptions.length === 0 ||
      allSurveyOptions.length === 0
    ) {
      return
    }
    const { categoryId, categoryName, surveyName, surveyId: stateSurveyId } = location.state
    if (!categoryId) return

    const catIdStr = String(categoryId)
    const categoryExists = categoryOptions.some(c => String(c.id) === catIdStr)
    if (!categoryExists) return

    validation.setFieldValue("categoryId", categoryId)

    let surveyIdToSet = null
    if (stateSurveyId && allSurveyOptions.some(s => String(s.id) === String(stateSurveyId))) {
      surveyIdToSet = stateSurveyId
    }
    if (!surveyIdToSet && surveyName) {
      const survey = allSurveyOptions.find(
        s => s.category_name === categoryName && (s.survey_name === surveyName || s.name === surveyName)
      )
      if (survey) surveyIdToSet = survey.id
    }
    if (!surveyIdToSet && surveyName) {
      const survey = allSurveyOptions.find(
        s => s.survey_name === surveyName || s.name === surveyName
      )
      if (survey) surveyIdToSet = survey.id
    }
    if (surveyIdToSet != null) {
      validation.setFieldValue("surveyId", surveyIdToSet)
    }
    hasAppliedListState.current = true
  }, [pageView, location.state, categoryOptions, allSurveyOptions])

  // Auto-calculate estimate cost based on panel selection + total questions
  const getPanelPricingTable = (panelName) => {
    if (!panelName) return []
    if (panelName.includes("Mid-Up") || panelName.includes("mid-up")) {
      return [
        { maxQ: 30,  filter: 1,   reward: 25, additional: 3 },
        { maxQ: 60,  filter: 1.5, reward: 30, additional: 5 },
        { maxQ: 80,  filter: 2.0, reward: 35, additional: 5 },
        { maxQ: 120, filter: 2.0, reward: 45, additional: 5 },
        { maxQ: 150, filter: 2.5, reward: 60, additional: 8 },
        { maxQ: 200, filter: 2.5, reward: 70, additional: 10 },
        { maxQ: Infinity, filter: 3, reward: 80, additional: 10 },
      ]
    }
    if (panelName.includes("Common") || panelName.includes("common") || panelName.includes("Consumer")) {
      return [
        { maxQ: 30,  filter: 0, reward: 12, additional: 2 },
        { maxQ: 60,  filter: 0, reward: 15, additional: 2 },
        { maxQ: 80,  filter: 0, reward: 18, additional: 3 },
        { maxQ: 120, filter: 0, reward: 20, additional: 3 },
        { maxQ: 150, filter: 0, reward: 25, additional: 5 },
        { maxQ: 200, filter: 0, reward: 30, additional: 5 },
        { maxQ: Infinity, filter: 0, reward: 40, additional: 5 },
      ]
    }
    return []
  }

  useEffect(() => {
    const panelOption = validation.values.panelOption
    const totalSurveyQ = Number(validation.values.totalSurveyQuestions) || 0
    const filterQ = Number(validation.values.setFilterQuestion) || 0
    const totalQuestions = totalSurveyQ + filterQ

    const pricingTable = getPanelPricingTable(panelOption)
    if (pricingTable.length === 0 || totalQuestions <= 0) {
      validation.setFieldValue("estimateCostFee", "0.00")
      validation.setFieldValue("estimateCostBasicRewards", "0.00")
      validation.setFieldValue("estimateCostAddOnRewards", "0.00")
      validation.setFieldValue("estimateCostTotalRewards", "0.00")
      validation.setFieldValue("estimateCostManagementFee", "0.00")
      validation.setFieldValue("estimateCostTotal", "0.00")
      return
    }

    const tier = pricingTable.find(row => totalQuestions <= row.maxQ) || pricingTable[pricingTable.length - 1]

    const filterFee = tier.filter
    const basicRewards = tier.reward
    const addOnRewards = tier.additional
    const n = Number(validation.values.totalMaximumSamples) || 0
    const totalRewards = parseFloat(((n * basicRewards) + (2 * n * filterFee) + (0.7 * n * addOnRewards)).toFixed(2))
    const managementFee = parseFloat((totalRewards * 0.1).toFixed(2))
    const subtotal = totalRewards + managementFee
    const totalEstimateFee = parseFloat((subtotal + subtotal * 0.1).toFixed(2))

    validation.setFieldValue("estimateCostFee", filterFee.toFixed(2))
    validation.setFieldValue("estimateCostBasicRewards", basicRewards.toFixed(2))
    validation.setFieldValue("estimateCostAddOnRewards", addOnRewards.toFixed(2))
    validation.setFieldValue("estimateCostTotalRewards", totalRewards.toFixed(2))
    validation.setFieldValue("estimateCostManagementFee", managementFee.toFixed(2))
    validation.setFieldValue("estimateCostTotal", totalEstimateFee.toFixed(2))
  }, [validation.values.panelOption, validation.values.totalSurveyQuestions, validation.values.setFilterQuestion, validation.values.totalMaximumSamples])

  // Calculate totalSamples and totalMaximumSamples from demographic cards
  useEffect(() => {
    const totalTarget = demographicCards.reduce((sum, card) => {
      const value = Number(card.groupTargetNumber) || 0
      return sum + value
    }, 0)
    
    const totalMaximum = demographicCards.reduce((sum, card) => {
      const value = Number(card.groupMaximumSamples) || 0
      return sum + value
    }, 0)
    
    validation.setFieldValue("totalSamples", totalTarget > 0 ? String(totalTarget) : "")
    validation.setFieldValue("totalMaximumSamples", totalMaximum > 0 ? String(totalMaximum) : "")
  }, [demographicCards])

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleNextStep = () => {
    if (currentStep < 4) {
      // Validate current step before proceeding
      if (currentStep === 1) {
        const step1Errors = []
        if (!validation.values.countryTarget || validation.errors.countryTarget) step1Errors.push("countryTarget")
        
        // panelOption must be selected to proceed
        if (!validation.values.panelOption || validation.errors.panelOption) {
          step1Errors.push("panelOption")
        }
        
        if (step1Errors.length > 0) {
          step1Errors.forEach(field => {
            validation.setFieldTouched(field, true)
          })
          return
        }
      } else if (currentStep === 2) {
        const step2Errors = []
        // Validate Select Samples
        if (!validation.values.sampleType || validation.errors.sampleType) step2Errors.push("sampleType")
        if (!validation.values.totalSamples || validation.errors.totalSamples) step2Errors.push("totalSamples")
        if (!validation.values.totalMaximumSamples || validation.errors.totalMaximumSamples) step2Errors.push("totalMaximumSamples")
        
        // Validate Contact Information
        if (!validation.values.company || validation.errors.company) step2Errors.push("company")
        if (!validation.values.personInCharge || validation.errors.personInCharge) step2Errors.push("personInCharge")
        if (!validation.values.email || validation.errors.email) step2Errors.push("email")
        if (!validation.values.telephone || validation.errors.telephone) step2Errors.push("telephone")

        // Validate Demographic Cards (At least 1 card required)
        if (demographicCards.length === 0) {
          setDemographicErrorMessage("Please add at least one demographic card.")
          setShowDemographicErrorModal(true)
          return
        }

        // Validate that each demographic card has groupTargetNumber and groupMaximumSamples
        const cardsWithMissingFields = demographicCards.filter(card => 
          !card.groupTargetNumber || !card.groupMaximumSamples || 
          Number(card.groupTargetNumber) <= 0 || Number(card.groupMaximumSamples) <= 0
        )
        
        if (cardsWithMissingFields.length > 0) {
          setDemographicErrorMessage("Each demographic card must have valid Group Target Number and Group Maximum Samples.")
          setShowDemographicErrorModal(true)
          return
        }

        // Validate that each demographic card has at least one selection or requirement
        const isCardValid = (card) => {
          // Check if specificRequirement is filled
          if (card.specificRequirement && card.specificRequirement.trim()) return true

          // Check categoriesConfig
          for (const cat of categoriesConfig) {
            if (card[cat.key]) {
              const selectedOptions = Object.values(card[cat.key]).filter(val => val === true)
              if (selectedOptions.length > 0) return true
            }
          }

          // Check additionalBasicCategories
          for (const cat of additionalBasicCategories) {
            if (card[cat.key]) {
              const selectedOptions = Object.values(card[cat.key]).filter(val => val === true)
              if (selectedOptions.length > 0) return true
            }
          }

          // Check carRelatedCategories
          for (const cat of carRelatedCategories) {
            if (card[cat.key]) {
              const selectedOptions = Object.values(card[cat.key]).filter(val => val === true)
              if (selectedOptions.length > 0) return true
            }
          }

          // Check plannedPurchaseCategories
          for (const cat of plannedPurchaseCategories) {
            if (card[cat.key]) {
              const selectedOptions = Object.values(card[cat.key]).filter(val => val === true)
              if (selectedOptions.length > 0) return true
            }
          }

          // Check cities
          if (card.cities) {
            const selectedCities = Object.values(card.cities).filter(val => val === true)
            if (selectedCities.length > 0) return true
          }

          return false
        }

        const allCardsValid = demographicCards.every(isCardValid)
        if (!allCardsValid) {
          setDemographicErrorMessage("Each demographic card must have at least one selection or requirement.")
          setShowDemographicErrorModal(true)
          return
        }

        if (step2Errors.length > 0) {
          step2Errors.forEach(field => {
            validation.setFieldTouched(field, true)
          })
          return
        }
      } else if (currentStep === 3) {
        const step3Errors = []
        if (!validation.values.categoryId || validation.errors.categoryId) step3Errors.push("categoryId")
        if (!validation.values.surveyId || validation.errors.surveyId) step3Errors.push("surveyId")
        if (validation.values.setFilterQuestion === "" || validation.errors.setFilterQuestion) step3Errors.push("setFilterQuestion")

        if (!validation.values.campaignName || validation.errors.campaignName) step3Errors.push("campaignName")
        if (!validation.values.startDate || validation.errors.startDate) step3Errors.push("startDate")
        if (!validation.values.endDate || validation.errors.endDate) step3Errors.push("endDate")

        if (step3Errors.length > 0) {
          step3Errors.forEach(field => {
            validation.setFieldTouched(field, true)
          })
          return
        }
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const getSelectedPanelImage = () => {
    const selected = allPanelOptions.find(p => p.value === validation.values.panelOption)
    return selected ? selected.image : ""
  }

  // Demographic Card Functions
  const addDemographicCard = () => {
    const initialState = createInitialDemographicState()
    const newCard = {
      id: Date.now(),
      isOpen: true,
      ...initialState,
    }
    setDemographicCards([...demographicCards, newCard])
  }

  const removeDemographicCard = (cardId) => {
    setDemographicCards(demographicCards.filter(card => card.id !== cardId))
  }

  const toggleDemographicCard = (cardId) => {
    setDemographicCards(demographicCards.map(card => 
      card.id === cardId ? { ...card, isOpen: !card.isOpen } : card
    ))
  }

  const updateDemographicCard = (cardId, field, value) => {
    setDemographicCards(demographicCards.map(card => 
      card.id === cardId ? { ...card, [field]: value } : card
    ))
  }

  const PageAction = pageView === "create" ? "Create" : pageView === "duplicate" ? "Duplicate" : "Edit"
  document.title = `Survey Order Campaign | ${PageAction} | ` + process.env.REACT_APP_CMS_TITLE

  if (isLoading) {
    return <Spinners setLoading={setIsLoading} />
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          title={"Survey Order Campaign"}
          breadcrumbItems={[
            { title: "Survey Order Campaign", link: "survey-order-campaign" },
            { title: `${PageAction} Campaign`, link: pageView },
          ]}
        />

        {/* Step Indicator - Show only for steps 1-3 */}
        {currentStep <= 3 && (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            {PageAction} Campaign <span style={{ color: "#34c38f" }}>{currentStep}</span><span style={{ color: "#74788d" }}>/3</span>
          </h4>
        )}

        {/* Step 4 Header - Create Campaign */}
        {currentStep === 4 && (
          <h4 className="mb-4" style={{ fontWeight: "600" }}>
            Create Campaign
          </h4>
        )}

          {/* Step 1: Select Target & Panel */}
          {currentStep === 1 && (
            <Step1
              validation={validation}
              pageView={pageView}
              countryOptions={countryOptions}
              panelOptions={panelOptions}
              showPanelModal={showPanelModal}
              setShowPanelModal={setShowPanelModal}
              getSelectedPanelImage={getSelectedPanelImage}
              countriesWithPanels={countriesWithPanels}
            />
          )}

          {/* Step 2: Select Samples, Demographic & Contact */}
          {currentStep === 2 && (
            <Step2
              validation={validation}
              pageView={pageView}
              sampleTypeOptions={sampleTypeOptions}
              demographicCards={demographicCards}
              addDemographicCard={addDemographicCard}
              removeDemographicCard={removeDemographicCard}
              toggleDemographicCard={toggleDemographicCard}
              updateDemographicCard={updateDemographicCard}
              countryTarget={validation.values.countryTarget}
            />
          )}

          {/* Step 3: Review & Confirm */}
          {currentStep === 3 && (
            <Step3 
              pageView={pageView} 
              validation={validation}
              categoryOptions={categoryOptions}
              surveyOptions={filteredSurveyOptions}
            />
          )}

          {/* Step 4: Estimate Cost */}
          {currentStep === 4 && (
            <EstimateCost
              pageView={pageView}
              validation={validation}
              onBack={handlePrevStep}
              onCancel={() => setShowCancelModal(true)}
              onSaveDraft={() => {
                validation.setFieldValue("status", "draft")
                setTimeout(() => validation.handleSubmit(), 0)
              }}
              onConfirm={() => {
                setShowConfirmSendModal(true)
              }}
          />
        )}

        {/* Navigation Buttons - Show only for steps 1-3 */}
        {currentStep <= 3 && (
          <Row className="mt-4">
            <Col style={{ display: "flex", justifyContent: "space-between" }}>
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  color="secondary"
                  className="btn btn-secondary"
                  onClick={currentStep === 1 ? () => navigate("/survey-order-campaign") : handlePrevStep}
                >
                  Back
                </Button>
                {currentStep === 1 && pageView === "update" && (
                  <Button
                    type="button"
                    color="danger"
                    className="btn btn-danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              <div>
                {currentStep < 3 ? (
                  <Button
                    type="button"
                    color="success"
                    className="btn btn-success"
                    onClick={handleNextStep}
                    disabled={pageView === "view"}
                  >
                    Next
                  </Button>
                ) : (
                  pageView !== "view" && (
                    <Button
                      type="button"
                      color="success"
                      className="btn btn-success"
                      onClick={handleNextStep}
                    >
                      Estimate Cost
                    </Button>
                  )
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Confirm Send Order Modal */}
        <ConfirmSendOrderModal
          isOpen={showConfirmSendModal}
          toggle={() => setShowConfirmSendModal(false)}
          onSendNow={() => {
            setShowConfirmSendModal(false)
            validation.setFieldValue("status", "In Progress")
            setTimeout(() => validation.handleSubmit(), 0)
          }}
          onSendWithin24Hrs={() => {
            setShowConfirmSendModal(false)
            validation.setFieldValue("status", "pending")
            setTimeout(() => validation.handleSubmit(), 0)
          }}
        />

        {/* Draft Success Modal */}
        <DraftSuccessModal
          isOpen={showDraftSuccessModal}
          toggle={() => {
            setShowDraftSuccessModal(false)
            navigate("/survey-order-campaign")
          }}
        />

        {/* Cancel Confirmation Modal */}
        <CancelModal
          show={showCancelModal}
          onConfirm={() => {
            setShowCancelModal(false)
            validation.setFieldValue("status", "cancelled")
            setTimeout(() => validation.handleSubmit(), 0)
          }}
          onCancel={() => setShowCancelModal(false)}
        />

        {/* Demographic Validation Error Modal */}
        <DemographicErrorModal
          show={showDemographicErrorModal}
          message={demographicErrorMessage}
          onConfirm={() => {
            setShowDemographicErrorModal(false)
          }}
        />

      </Container>
    </div>
  )
}

SurveyOrderCampaignTemplate.propTypes = {
  t: PropTypes.any,
  pageView: PropTypes.string,
}

export default withTranslation()(SurveyOrderCampaignTemplate)
